/**
 * SensorStation Cart Bundle Manager
 * Version: 5.9.1
 * Last Updated: 2026-08-05
 *
 * Changelog:
 * - 5.9.1: Show customer-visible line item properties inside bundle cards
 * - 5.9.0: Support linked BlūBat tag, Motus registration, and data bundles
 * - 5.8.1: Ignore non-bundle configuration IDs and orphaned bundle components
 * - 5.8.0: Support for slide-out cart drawer (not just /cart page)
 * - 5.7.0: Fixed row matching - use data-item key instead of index-based matching
 * - 5.6.0: Explicit row visibility control; rows hidden by default, JS reveals correct ones
 * - 5.5.0: requestAnimationFrame loop for instant detection; simplified logic
 * - 5.4.0: CSS in theme.liquid head for instant hiding; aggressive polling backup
 */

(function() {
  'use strict';

  const VERSION = '5.9.1';
  const DEBUG = false;
  let isProcessing = false;
  let lastProcessedState = '';
  let lastDOMState = '';
  let isCartDrawerOpen = false;

  async function getCartData() {
    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function getVisibleProperties(properties) {
    if (!properties || typeof properties !== 'object') return [];

    return Object.entries(properties)
      .filter(([name, value]) => (
        name &&
        !name.startsWith('_') &&
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      ))
      .map(([name, value]) => ({
        name: String(name),
        value: String(value)
      }));
  }

  function renderVisibleProperties(properties) {
    if (!Array.isArray(properties) || properties.length === 0) return '';

    const propertyRows = properties.map(property => `
      <div class="ss-bundle-item-property">
        <dt>${escapeHtml(property.name)}:</dt>
        <dd>${escapeHtml(property.value)}</dd>
      </div>
    `).join('');

    return `<dl class="ss-bundle-item-properties">${propertyRows}</dl>`;
  }

  function parseBundleQuantity(value) {
    const quantity = Number(value);
    return Number.isInteger(quantity) && quantity >= 1 ? quantity : null;
  }

  function getBundlesFromCart(cartData) {
    const bundles = {};
    if (!cartData || !cartData.items) return bundles;

    cartData.items.forEach((item, index) => {
      const bundleId = item.properties?._bundle_id;
      const bundleRole = item.properties?._bundle_role;
      if (!bundleId || !['parent', 'component'].includes(bundleRole)) return;

      if (!bundles[bundleId]) {
        bundles[bundleId] = { id: bundleId, items: [], baseQty: 1 };
      }

      bundles[bundleId].items.push({
        lineIndex: index + 1,
        key: item.key,
        quantity: item.quantity,
        multiplier: parseInt(item.properties?._bundle_multiplier) || 1,
        role: bundleRole,
        title: item.product_title,
        price: item.price,
        image: item.image,
        visibleProperties: getVisibleProperties(item.properties)
      });

      if (bundleRole === 'parent') {
        bundles[bundleId].baseQty = item.quantity;
      }
    });

    Object.keys(bundles).forEach(bundleId => {
      if (!bundles[bundleId].items.some(item => item.role === 'parent')) {
        delete bundles[bundleId];
      }
    });

    return bundles;
  }

  function getAllCartRows() {
    return Array.from(document.querySelectorAll('.cart__table-row, .cart-item, .cart__row'))
      .filter(el => el.querySelector('a[href*="/products/"]'));
  }

  // Find a cart row by its line item key (from data-item attribute)
  function findRowByKey(rows, key) {
    return rows.find(row => row.dataset.item === key);
  }

  function cleanupUI() {
    // Remove bundle containers
    document.querySelectorAll('.ss-bundle-container').forEach(el => el.remove());
    // Reset all row classes
    document.querySelectorAll('.ss-hidden-bundle-row, .ss-visible-row').forEach(el => {
      el.classList.remove('ss-hidden-bundle-row', 'ss-visible-row');
    });
  }

  function createBundleUI(bundle) {
    const container = document.createElement('div');
    container.className = 'ss-bundle-container';
    container.dataset.bundleId = bundle.id;

    const isBlubat = bundle.id.startsWith('BB-');
    const bundleTitle = isBlubat ? 'BlūBat Configuration' : 'SensorStation Configuration';

    let total = 0;
    bundle.items.forEach(i => total += i.price * i.quantity);

    const sortedItems = [...bundle.items].sort((a, b) => {
      if (a.role === 'parent') return -1;
      if (b.role === 'parent') return 1;
      return a.lineIndex - b.lineIndex;
    });

    let itemsHtml = sortedItems.map(item => {
      const isParent = item.role === 'parent';
      const propertiesHtml = renderVisibleProperties(item.visibleProperties);
      return `
        <div class="ss-bundle-item ${isParent ? 'is-parent' : ''}">
          <img class="ss-bundle-item-image" src="${item.image || ''}" alt="">
          <div class="ss-bundle-item-details">
            <div class="ss-bundle-item-title">${item.title}${isParent ? ' <span class="ss-bundle-item-parent-badge">Main</span>' : ''}</div>
            <div class="ss-bundle-item-meta">$${(item.price/100).toFixed(2)} each</div>
            ${propertiesHtml}
          </div>
          <div class="ss-bundle-item-qty">× ${item.quantity}</div>
          <div class="ss-bundle-item-price">$${(item.price * item.quantity / 100).toFixed(2)}</div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="ss-bundle-header">
        <div>
          <div class="ss-bundle-title">${bundleTitle}</div>
          <div class="ss-bundle-id">${bundle.id}</div>
        </div>
        <div class="ss-bundle-actions">
          <div class="ss-bundle-qty-control">
            <span class="ss-bundle-qty-label">Qty:</span>
            <button class="ss-bundle-qty-btn" data-action="decrease" aria-label="Decrease ${bundleTitle} quantity">−</button>
            <input
              class="ss-bundle-qty-input"
              type="number"
              min="1"
              step="1"
              inputmode="numeric"
              value="${bundle.baseQty}"
              aria-label="${bundleTitle} quantity"
            >
            <button class="ss-bundle-qty-btn" data-action="increase" aria-label="Increase ${bundleTitle} quantity">+</button>
          </div>
          <button class="ss-bundle-remove">Remove</button>
        </div>
      </div>
      <div class="ss-bundle-items">${itemsHtml}</div>
      <div class="ss-bundle-footer">
        <span class="ss-bundle-total-label">Bundle Total</span>
        <span class="ss-bundle-total-price">$${(total/100).toFixed(2)}</span>
      </div>
    `;

    const quantityInput = container.querySelector('.ss-bundle-qty-input');
    const commitQuantity = () => {
      const requestedQty = parseBundleQuantity(quantityInput.value);

      if (requestedQty === null) {
        quantityInput.setCustomValidity('Enter a whole number of 1 or more.');
        quantityInput.reportValidity();
        quantityInput.focus();
        return;
      }

      quantityInput.setCustomValidity('');
      if (requestedQty === bundle.baseQty) return;
      return updateBundle(bundle, requestedQty);
    };

    quantityInput.oninput = () => quantityInput.setCustomValidity('');
    quantityInput.onchange = () => {
      const focusedAction = document.activeElement?.dataset?.action;
      if (focusedAction === 'decrease' || focusedAction === 'increase') return;
      return commitQuantity();
    };
    quantityInput.onkeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        return commitQuantity();
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        quantityInput.value = String(bundle.baseQty);
        quantityInput.setCustomValidity('');
        quantityInput.blur();
      }
    };

    // Event handlers
    container.querySelector('[data-action="decrease"]').onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const currentQty = parseBundleQuantity(quantityInput.value) ?? bundle.baseQty;
      updateBundle(bundle, currentQty - 1);
    };
    container.querySelector('[data-action="increase"]').onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const currentQty = parseBundleQuantity(quantityInput.value) ?? bundle.baseQty;
      updateBundle(bundle, currentQty + 1);
    };
    container.querySelector('.ss-bundle-remove').onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      updateBundle(bundle, 0);
    };

    return container;
  }

  async function updateBundle(bundle, newQty) {
    if (!Number.isInteger(newQty) || newQty < 0) return;
    const removePrompt = bundle.id.startsWith('BB-')
      ? 'Remove this BlūBat configuration?'
      : 'Remove this SensorStation configuration?';
    if (newQty === 0 && !confirm(removePrompt)) return;

    const container = document.querySelector(`[data-bundle-id="${bundle.id}"]`);
    if (container) {
      container.style.opacity = '0.5';
      container.style.pointerEvents = 'none';
    }

    const updates = {};
    bundle.items.forEach(item => {
      updates[item.key] = newQty * item.multiplier;
    });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      window.location.reload();
    } catch (e) {
      alert('Error updating cart');
      if (container) {
        container.style.opacity = '1';
        container.style.pointerEvents = 'auto';
      }
    }
  }

  async function processCart() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cartData = await getCartData();
      if (!cartData) {
        isProcessing = false;
        return;
      }

      const bundles = getBundlesFromCart(cartData);
      const bundleIds = Object.keys(bundles);

      // Create state signature based on cart data AND current DOM
      const allRows = getAllCartRows();
      const domSignature = allRows.map(r => r.innerHTML.substring(0, 100)).join('|');
      const stateSignature = JSON.stringify({
        bundles: bundleIds,
        items: cartData.items.map(i => i.key + ':' + i.quantity)
      });

      // Check if we need to process
      const existingContainers = document.querySelectorAll('.ss-bundle-container').length;

      // Skip if nothing has changed
      if (stateSignature === lastProcessedState &&
          domSignature === lastDOMState &&
          existingContainers === bundleIds.length) {
        isProcessing = false;
        return;
      }

      if (DEBUG) console.log(`[BundleManager v${VERSION}] Processing: ${bundleIds.length} bundles, ${cartData.items.length} items, ${allRows.length} rows`);

      // Clean up existing UI
      cleanupUI();

      // Get fresh row list after cleanup
      const freshRows = getAllCartRows();

      // Build a set of bundle item keys for quick lookup
      const bundleItemKeys = new Set();
      for (const bundleId of bundleIds) {
        bundles[bundleId].items.forEach(item => {
          bundleItemKeys.add(item.key);
        });
      }

      if (DEBUG) {
        console.log('[BundleManager] Bundle item keys:', Array.from(bundleItemKeys));
        console.log('[BundleManager] DOM row keys:', freshRows.map(r => r.dataset.item));
      }

      // Process each bundle - insert UI and hide rows
      for (const bundleId of bundleIds) {
        const bundle = bundles[bundleId];

        // Find the first bundle row by key (use first item's key)
        const sortedItems = [...bundle.items].sort((a, b) => a.lineIndex - b.lineIndex);
        const firstRow = findRowByKey(freshRows, sortedItems[0].key);

        if (firstRow) {
          const ui = createBundleUI(bundle);
          firstRow.parentNode.insertBefore(ui, firstRow);

          // Hide all bundle rows by key
          bundle.items.forEach(item => {
            const row = findRowByKey(freshRows, item.key);
            if (row) {
              row.classList.add('ss-hidden-bundle-row');
            } else if (DEBUG) {
              console.warn(`[BundleManager] Could not find row for key: ${item.key}`);
            }
          });
        } else if (DEBUG) {
          console.warn(`[BundleManager] Could not find first row for bundle: ${bundleId}`);
        }
      }

      // Show non-bundle rows explicitly (match by key, not index)
      freshRows.forEach(row => {
        const rowKey = row.dataset.item;
        if (!bundleItemKeys.has(rowKey) && !row.classList.contains('ss-hidden-bundle-row')) {
          row.classList.add('ss-visible-row');
        }
      });

      // Update state tracking
      lastProcessedState = stateSignature;
      lastDOMState = domSignature;

      if (DEBUG) console.log(`[BundleManager] Done: ${bundleIds.length} bundles created, ${freshRows.length - bundleItemKeys.size} regular items visible`);

    } catch (e) {
      console.error('[BundleManager] Error:', e);
    }

    isProcessing = false;
  }

  function injectStyles() {
    if (document.getElementById('ss-bundle-styles')) return;
    const css = `
      .ss-bundle-container{border:2px solid #2c5aa0;border-radius:8px;margin:15px 0;overflow:hidden;background:#fff;visibility:visible!important;height:auto!important}
      .ss-bundle-header{background:linear-gradient(135deg,#2c5aa0 0%,#1e3d6f 100%);color:#fff;padding:12px 20px;display:flex;justify-content:space-between;align-items:center}
      .ss-bundle-title{font-weight:600;font-size:1rem}
      .ss-bundle-id{font-size:.8rem;opacity:.85;font-family:monospace}
      .ss-bundle-actions{display:flex;align-items:center;gap:15px}
      .ss-bundle-qty-control{display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.15);padding:5px 10px;border-radius:5px}
      .ss-bundle-qty-label{font-size:.85rem}
      .ss-bundle-qty-btn{width:28px;height:28px;border:none;background:rgba(255,255,255,.25);color:#fff;border-radius:4px;cursor:pointer;font-size:1.1rem;font-weight:700;display:flex;align-items:center;justify-content:center}
      .ss-bundle-qty-btn:hover{background:rgba(255,255,255,.4)}
      .ss-bundle-qty-input{width:52px;height:28px;border:1px solid rgba(255,255,255,.7);border-radius:4px;background:#fff;color:#1e3d6f;text-align:center;font:inherit;font-weight:600;appearance:textfield;-moz-appearance:textfield}
      .ss-bundle-qty-input::-webkit-inner-spin-button,.ss-bundle-qty-input::-webkit-outer-spin-button{margin:0;appearance:none;-webkit-appearance:none}
      .ss-bundle-qty-input:focus-visible{outline:2px solid #fff;outline-offset:2px}
      .ss-bundle-remove{background:rgba(220,53,69,.9);color:#fff;border:none;padding:6px 12px;border-radius:4px;cursor:pointer;font-size:.85rem}
      .ss-bundle-remove:hover{background:#c82333}
      .ss-bundle-items{padding:0}
      .ss-bundle-item{display:grid;grid-template-columns:70px 1fr auto auto;gap:15px;padding:15px 20px;border-bottom:1px solid #eee;align-items:center}
      .ss-bundle-item:last-child{border-bottom:none}
      .ss-bundle-item.is-parent{background:#f8fbff}
      .ss-bundle-item-image{width:70px;height:70px;object-fit:contain;border-radius:4px;background:#f5f5f5}
      .ss-bundle-item-details{min-width:0}
      .ss-bundle-item-title{font-weight:500;margin-bottom:3px;color:#333}
      .ss-bundle-item-meta{font-size:.85rem;color:#666}
      .ss-bundle-item-properties{margin:8px 0 0;font-size:.8rem;line-height:1.4;color:#555}
      .ss-bundle-item-property{display:grid;grid-template-columns:max-content minmax(0,1fr);gap:4px;margin:0}
      .ss-bundle-item-property dt{font-weight:600}
      .ss-bundle-item-property dd{margin:0;overflow-wrap:anywhere}
      .ss-bundle-item-parent-badge{display:inline-block;background:#2c5aa0;color:#fff;font-size:.7rem;padding:2px 6px;border-radius:3px;margin-left:8px}
      .ss-bundle-item-qty{text-align:center;color:#666;font-size:.9rem}
      .ss-bundle-item-price{text-align:right;font-weight:500;min-width:80px}
      .ss-bundle-footer{background:#f8f9fa;padding:12px 20px;display:flex;justify-content:space-between;align-items:center;border-top:2px solid #eee}
      .ss-bundle-total-label{font-weight:500;color:#666}
      .ss-bundle-total-price{font-weight:600;font-size:1.1rem;color:#2c5aa0}
      .ss-hidden-bundle-row{display:none!important}
      @media(max-width:600px){.ss-bundle-header{flex-direction:column;gap:10px;text-align:center}.ss-bundle-item{grid-template-columns:50px 1fr}.ss-bundle-item-image{width:50px;height:50px}}
    `;
    const style = document.createElement('style');
    style.id = 'ss-bundle-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Check if we should be active (on cart page OR cart drawer is open)
  function shouldBeActive() {
    const onCartPage = window.location.pathname.includes('/cart');
    const drawerVisible = document.body.classList.contains('cart--is-visible');
    return onCartPage || drawerVisible || isCartDrawerOpen;
  }

  // Main loop - runs continuously to detect DOM changes
  function checkLoop() {
    if (!shouldBeActive()) {
      requestAnimationFrame(checkLoop);
      return;
    }

    // Check for rows that haven't been processed yet (no ss-visible-row or ss-hidden-bundle-row class)
    const unprocessedRows = document.querySelectorAll(
      '.cart__table-row:not(.ss-visible-row):not(.ss-hidden-bundle-row), ' +
      '.cart-item:not(.ss-visible-row):not(.ss-hidden-bundle-row), ' +
      '.cart__row:not(.ss-visible-row):not(.ss-hidden-bundle-row)'
    );

    // Filter to only actual product rows
    const actualUnprocessed = Array.from(unprocessedRows)
      .filter(el => el.querySelector('a[href*="/products/"]'));

    // If there are unprocessed rows, we need to run
    if (actualUnprocessed.length > 0) {
      if (DEBUG) console.log(`[BundleManager] Detected ${actualUnprocessed.length} unprocessed rows`);
      processCart();
    }

    // Continue the loop
    requestAnimationFrame(checkLoop);
  }

  // Initialize
  function init() {
    console.log(`[BundleManager] v${VERSION} initializing...`);
    injectStyles();

    // Listen for all possible cart events - reset state to force reprocess
    ['theme:cart:change', 'theme:cart:refresh', 'theme:cart:update', 'theme:product:add'].forEach(evt => {
      document.addEventListener(evt, () => {
        if (DEBUG) console.log(`[BundleManager] ${evt} received - forcing reprocess`);
        lastProcessedState = '';
        lastDOMState = '';
        if (shouldBeActive()) processCart();
      });
    });

    // Listen for cart drawer open/close events
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('theme:cart-drawer:show', () => {
        if (DEBUG) console.log('[BundleManager] Cart drawer opening');
        isCartDrawerOpen = true;
        lastProcessedState = '';
        lastDOMState = '';
        // Small delay to let drawer HTML render
        setTimeout(() => processCart(), 50);
      });
    }

    // Also listen on document for drawer events
    document.addEventListener('theme:cart-drawer:open', () => {
      if (DEBUG) console.log('[BundleManager] Cart drawer opened');
      isCartDrawerOpen = true;
      lastProcessedState = '';
      lastDOMState = '';
      processCart();
    });

    document.addEventListener('theme:cart-drawer:close', () => {
      if (DEBUG) console.log('[BundleManager] Cart drawer closed');
      isCartDrawerOpen = false;
    });

    // Initial process if on cart page
    if (window.location.pathname.includes('/cart')) {
      processCart();
    }

    // Start continuous monitoring for DOM changes
    requestAnimationFrame(checkLoop);
  }

  // Start when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle back/forward cache
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      lastProcessedState = '';
      lastDOMState = '';
      processCart();
    }
  });

  // Expose for debugging
  window.BundleManager = {
    processCart,
    createBundleUI,
    getBundlesFromCart,
    getVisibleProperties,
    parseBundleQuantity,
    renderVisibleProperties,
    VERSION
  };

})();
